import { useEffect, useRef, useState, type HTMLAttributes, type TdHTMLAttributes, type ThHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

export function Table({ className, ...props }: HTMLAttributes<HTMLTableElement>) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [scrolls, setScrolls] = useState(false);

  useEffect(() => {
    const element = wrapRef.current;
    if (element === null) return;
    const update = () => setScrolls(element.scrollWidth > element.clientWidth + 1);
    update();
    window.addEventListener("resize", update);
    // Resize is not the only way the wrapper starts overflowing: the table's rows arrive
    // AFTER mount on every react-query surface, and a content-driven overflow fires no
    // window resize event. Observing the wrapper itself (disconnected on cleanup) lets a
    // late-loading table gain its keyboard tab stop the moment it truly overflows.
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    observer?.observe(element);
    return () => {
      window.removeEventListener("resize", update);
      observer?.disconnect();
    };
  }, []);

  return (
    <div
      ref={wrapRef}
      // `relative` makes this wrapper the containing block for the absolutely positioned
      // sr-only cells some tables carry in their LAST column: without it those spans take
      // their pre-clip static position (inside the unclipped table geometry), escape the
      // scroll clip, and widen the whole document at narrow widths. This is a containing
      // block fix only - no visual change inside the DS table grammar.
      className="relative w-full overflow-x-auto"
      // When a narrow viewport or the browser's native zoom makes this wrapper actually
      // scroll, keyboard users must be able to reach the scrolled-off columns (axe
      // scrollable-region-focusable, serious; WCAG 2.1.1), so the wrapper becomes exactly
      // one tab stop ONLY while it truly overflows - desktop density keeps no extra stop.
      tabIndex={scrolls ? 0 : undefined}
    >
      <table className={cn("w-full caption-bottom text-sm", className)} {...props} />
    </div>
  );
}

export function TableHead({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return <thead className={cn("border-b border-line", className)} {...props} />;
}

export function TableBody({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody className={cn("divide-y divide-line", className)} {...props} />;
}

export function TableRow({ className, ...props }: HTMLAttributes<HTMLTableRowElement>) {
  return <tr className={cn("hover:bg-surface-subtle", className)} {...props} />;
}

export function TableHeaderCell({ className, ...props }: ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      scope="col"
      className={cn(
        "px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted",
        className,
      )}
      {...props}
    />
  );
}

export function TableCell({ className, ...props }: TdHTMLAttributes<HTMLTableCellElement>) {
  return <td className={cn("px-3 py-2.5 align-middle text-ink", className)} {...props} />;
}

export function TableCaption({ className, ...props }: HTMLAttributes<HTMLTableCaptionElement>) {
  return <caption className={cn("py-3 text-xs text-ink-muted", className)} {...props} />;
}
